import React, { createContext, useContext, useState } from 'react';
import classNames from 'classnames';
import { Button, Tooltip } from '..';
import { TOOLTIP_DATA_REQUEST_ATTR } from '../Tooltip/Tooltip';

import css from './Controls.css';
import { IconMenuDotsL } from '../../icons';

const ControlsContext = createContext(null);

const useControlsContext = () => {
    const context = useContext(ControlsContext);
    if (!context) {
        throw new Error('Controls.* must be rendered inside <Controls>');
    }
    return context;
};

export const Controls = ({
    children,
    handleToggle,
    rootClassName,
    tooltipClassName: tooltipClassNameProp,
    controlHolderProps = {},
    isOpened,
    menu,
}) => {
    return (
        <ControlsContext.Provider value={{ isOpened, handleToggle }}>
            <Tooltip
                isStatic
                tooltipClassName={classNames(css.controlsList, tooltipClassNameProp)}
                rootClassName={classNames(css.control, {
                    [css.controlOpened]: isOpened,
                    [rootClassName]: !!rootClassName,
                })}
                staticTooltipState={Boolean(isOpened)}
                {...controlHolderProps}
                content={menu}
            >
                {children}
            </Tooltip>
        </ControlsContext.Provider>
    );
};

const Trigger = ({ className, children, ...restProps }) => {
    const { handleToggle } = useControlsContext();

    return (
        <Button
            onClick={handleToggle}
            type="button"
            className={classNames(css.ellipsis, className)}
            data-role={TOOLTIP_DATA_REQUEST_ATTR}
            {...restProps}
        >
            {children || <IconMenuDotsL />}
        </Button>
    );
};

const Item = ({ children, onClick, icon, allowed = true, className, ...restProps }) => {
    if (!allowed) return null;

    return (
        <li onClick={onClick} className={className} {...restProps}>
            {icon}
            {typeof children === 'string' ? <span>{children}</span> : children}
        </li>
    );
};

const Menu = ({ children, className, controlOptions, ...restProps }) => {
    const { isOpened } = useControlsContext();

    if (!isOpened) return null;

    if (Array.isArray(controlOptions) && controlOptions.length > 0) {
        return (
            <ul>
                {controlOptions.map(
                    ({ label, action = () => null, icon = null, allowed = true }, i) => (
                        <Item key={i} icon={icon} onClick={action} allowed={allowed}>
                            {label}
                        </Item>
                    )
                )}
            </ul>
        );
    }

    return (
        <ul className={className} {...restProps}>
            {children}
        </ul>
    );
};

Controls.Trigger = Trigger;
Controls.Menu = Menu;
Controls.Item = Item;

export default Controls;
